import { Badge, BadgeProps } from "@admin/components/atoms/Badge";
import { Button } from "@admin/components/atoms/Button";
import { DataTable, DataTableColumn } from "@admin/components/molecules/Table";
import { useCompany } from "@admin/hooks/useCompany";
import {
  ControlPeriod,
  FoodControlPoint,
  InventoryTypes,
  UpsertModalType,
  useCommonStore,
} from "@enos-monorepo/lib";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { InventoryModal } from "../../control-points/components/MyLists/Modals/InventoryModal";
import { EditControlNameModal } from "./Modals/EditControlNameModal";
import { EditControlPeriodModal } from "./Modals/EditControlPeriodModal";
import { EditControlStatusModal } from "./Modals/EditControlStatusModal";
import { EditControlTypeModal } from "./Modals/EditControlTypeModal";

export function InventoryTable({
  foodControlPoints,
  controlPeriods,
  isPending = false,
}: {
  foodControlPoints: FoodControlPoint[];
  controlPeriods: ControlPeriod[];
  isPending: boolean;
}) {
  const { company } = useCompany();
  const { foodLocation: selectedLocationId } = useCommonStore();
  const { t } = useTranslation();
  const [selectedInventory, setSelectedInventory] =
    useState<FoodControlPoint>();
  const [isAddInventoryModalOpen, setIsAddInventoryModalOpen] = useState(false);
  const [isEditControlStatusModalOpen, setIsEditControlStatusModalOpen] =
    useState(false);
  const [isEditControlPeriodModalOpen, setIsEditControlPeriodModalOpen] =
    useState(false);
  const [isEditInventoryNameModalOpen, setIsEditInventoryNameModalOpen] =
    useState(false);
  const [isEditInventoryTypeModalOpen, setIsEditInventoryTypeModalOpen] =
    useState(false);

  const dataColumns: DataTableColumn<FoodControlPoint>[] = [
    {
      header: t("common.Name"),
      accessorKey: "name",
      enableSorting: true,
      sortingFn: "alphanumeric",
      cell: ({ row }) => (
        <div className="flex flex-row justify-between items-center">
          <div>{row.original.name}</div>
          <Button
            variant="clear"
            icon
            size="sm"
            onClick={() => setIsEditInventoryNameModalOpen(true)}
          >
            <i className="ki-filled ki-notepad-edit"></i>
          </Button>
        </div>
      ),
    },
    {
      header: t("food.Type"),
      accessorKey: "type",
      cell: ({ row }) => (
        <Badge
          size="lg"
          className="cursor-pointer"
          onClick={() => setIsEditInventoryTypeModalOpen(true)}
          type={
            InventoryTypes(t).find((it) => it.value === row.original.type)
              ?.color as BadgeProps["type"]
          }
        >
          {
            InventoryTypes(t).find((it) => it.value === row.original.type)
              ?.label
          }
        </Badge>
      ),
    },
    {
      header: t("common.Status"),
      accessorKey: "status",
      cell: ({ row }) => (
        <Badge
          size="lg"
          className={"hover:cursor-pointer"}
          type={row.original.isDeleted ? "danger" : "success"}
          onClick={() => setIsEditControlStatusModalOpen(true)}
        >
          {row.original.isDeleted ? "Inactive" : "Active"}
        </Badge>
      ),
    },
    {
      header: "Period",
      cell: ({ row }) => (
        <Badge
          type="dark"
          size="lg"
          className={"hover:cursor-pointer"}
          onClick={() => setIsEditControlPeriodModalOpen(true)}
        >
          {
            controlPeriods?.find(
              (period) => period.id === row.original.controlPeriodId,
            )?.description
          }
        </Badge>
      ),
    },
  ];

  return (
    <>
      <DataTable
        loading={isPending}
        columns={dataColumns}
        sorting={[{ id: "name", desc: false }]}
        onRowClick={(row) => setSelectedInventory(row)}
        data={foodControlPoints}
        title={t("navigation.Inventory")}
        isInfoIcon={false}
        header={
          <Button onClick={() => setIsAddInventoryModalOpen(true)} size="sm">
            <i className="ki-filled ki-plus-squared"></i>
            {t("food.Add New Inventory")}
          </Button>
        }
      />
      <InventoryModal
        companyId={company?.companyId}
        selectedLocationId={selectedLocationId}
        open={isAddInventoryModalOpen}
        onClose={() => setIsAddInventoryModalOpen(false)}
        type={UpsertModalType.Add}
        inventory={selectedInventory}
        controlPeriods={controlPeriods}
      />
      <EditControlNameModal
        inventory={selectedInventory}
        selectedLocationId={selectedLocationId}
        open={isEditInventoryNameModalOpen}
        onClose={() => setIsEditInventoryNameModalOpen(false)}
      />
      <EditControlTypeModal
        inventory={selectedInventory}
        selectedLocationId={selectedLocationId}
        open={isEditInventoryTypeModalOpen}
        onClose={() => setIsEditInventoryTypeModalOpen(false)}
      />
      <EditControlPeriodModal
        controlPoint={selectedInventory}
        open={isEditControlPeriodModalOpen}
        controlPeriods={controlPeriods}
        onClose={() => setIsEditControlPeriodModalOpen(false)}
      />
      <EditControlStatusModal
        open={isEditControlStatusModalOpen}
        onClose={() => setIsEditControlStatusModalOpen(false)}
        controlPoint={selectedInventory}
      />
    </>
  );
}
